import { INode, ISystem } from '../../helpers/interfaces';
import React, { FunctionComponent, useState, useEffect } from 'react';
import GridContainer from '../../../components/Grid/GridContainer';
import GridItem from '../../../components/Grid/GridItem';
import NodeShortStatus from '../../components/NodeShortStatus/NodeShortStatus';
import NodeSearchBar from '../../components/NodeSearchBar/NodeSearchBar';
import { connect } from "react-redux";
import { getNodeType, computeDeviceLostMap, sortNodesForDisplay, sortNonoperationalNodesForDisplay } from '../../helpers/nodeSorting';

type NodeCardsProps = {
	measuringDevices: INode[];
	infrastructureDevices: INode[];
	nonoperationalDevices: INode[];
	unknownDevices: INode[];
	system: ISystem;
	classes: any;
	exportStart: string;
	exportStop: string;
	dashboardFilteredNodes: number[];
	mobileView: boolean;
	deleteNodeSuccessfully: boolean;
	setCurrentNodeID: (nodeId: number) => void;
};

const NodeCards: FunctionComponent<NodeCardsProps> = ({
	measuringDevices,
	infrastructureDevices,
    nonoperationalDevices,
	unknownDevices,
	system,
	classes,
	exportStart,
	exportStop,
	setCurrentNodeID,
	mobileView,
	dashboardFilteredNodes,
	deleteNodeSuccessfully,
}) => {
	const [filteredMeasuringDevices, setFilteredMeasuringDevices] = useState<INode[]>([]);
	const [filteredInfrastructureDevices, setFilteredInfrastructureDevices] = useState<INode[]>([]);
	const [filteredNonoperationalDevices, setFilteredNonoperationalDevices] = useState<INode[]>([]);
	const [filteredUnknownDevices, setFilteredUnknownDevices] = useState<INode[]>([]);

	useEffect(() => {
		const currentFilteredMeasuringDevices = [];
		const currentFilteredInfrastructureDevices = [];
		const currentFilteredNonoperationalDevices = [];
		const currentFilteredUnknownDevices = [];
		dashboardFilteredNodes.forEach((filteredNode) => {
			const foundMeasuringDevice = measuringDevices.find(({ id }) => id === filteredNode);
			const foundInfrastructureDevice = infrastructureDevices.find(({ id }) => id === filteredNode);
			const foundNonoperationalDevice = nonoperationalDevices.find(({ id }) => id === filteredNode);
			const foundUnknownDevice = unknownDevices.find(({ id }) => id === filteredNode);
			if (foundMeasuringDevice) {
				currentFilteredMeasuringDevices.push(foundMeasuringDevice);
			}
			if (foundInfrastructureDevice) {
				currentFilteredInfrastructureDevices.push(foundInfrastructureDevice);
			}
			if (foundNonoperationalDevice) {
				currentFilteredNonoperationalDevices.push(foundNonoperationalDevice);
			}
			if (foundUnknownDevice) {
				currentFilteredUnknownDevices.push(foundUnknownDevice);
			}
		});
		setFilteredMeasuringDevices(currentFilteredMeasuringDevices);
		setFilteredInfrastructureDevices(currentFilteredInfrastructureDevices);
		setFilteredNonoperationalDevices(currentFilteredNonoperationalDevices);
		setFilteredUnknownDevices(currentFilteredUnknownDevices);
		if (
			!currentFilteredMeasuringDevices.length &&
			!currentFilteredInfrastructureDevices.length &&
			!currentFilteredNonoperationalDevices.length &&
			!currentFilteredUnknownDevices.length
		) {
			setFilteredMeasuringDevices(measuringDevices);
			setFilteredInfrastructureDevices(infrastructureDevices);
			setFilteredNonoperationalDevices(nonoperationalDevices);
			setFilteredUnknownDevices(unknownDevices);
		}
	}, [dashboardFilteredNodes, infrastructureDevices, measuringDevices, nonoperationalDevices, unknownDevices]);



	const allFilteredDevices = [...filteredMeasuringDevices, ...filteredInfrastructureDevices, ...filteredNonoperationalDevices, ...filteredUnknownDevices];
	const deviceLostMap = computeDeviceLostMap(allFilteredDevices);

	const renderNodeCardsContent = (nodes: INode[]): JSX.Element[] => {
		const nodesSorted = sortNodesForDisplay(nodes, deviceLostMap);
		const content = nodesSorted.map((node, i) => {
			let deviceTypeName = getNodeType(node);
			return (
				<GridItem key={`grid-item-${deviceTypeName}-${i}`} xs={12} sm={6} md={4} className={deviceTypeName}
				    style={{ padding: mobileView ? '0 15px' : (i+2)%3 ? '0 15px' : '0 7px'}}>
					<NodeShortStatus
						key={`${i}-${deviceTypeName}-short-status`}
						classes={classes}
						node={node}
						setCurrentNodeID={setCurrentNodeID}
						exportStart={exportStart}
						exportStop={exportStop}
						system={system}
						dataTestCounter={`${deviceTypeName}-${i}`}
						mobileView={mobileView}
					/>
				</GridItem>
			);
		});
		return content;
	};
	const renderNonoperationalNodeCardsContent = (nodes: INode[]): JSX.Element[] => {
		const nodesSorted = sortNonoperationalNodesForDisplay(nodes);
		const content = nodesSorted.map((node, i) => {
			let deviceTypeName = getNodeType(node);
			return (
				<GridItem key={`grid-item-${deviceTypeName}-${i}`} xs={12} sm={6} md={4} className={deviceTypeName}
						  style={{padding: mobileView ? '0 15px' : (i+2)%3 ? '0 15px' : '0 7px'}}>
					<NodeShortStatus
						key={`${i}-${deviceTypeName}-short-status`}
						classes={classes}
						node={node}
						setCurrentNodeID={setCurrentNodeID}
						exportStart={exportStart}
						exportStop={exportStop}
						system={system}
						dataTestCounter={`${deviceTypeName}-${i}`}
						mobileView={mobileView}
					/>
				</GridItem>
			);
		});
		return content;
	};

	const renderNodeBlock = (devices: INode[], title: string): JSX.Element => {
		if (devices.length === 0) {
			return null;
		} else {
			return (
				<>
					<GridItem xs={12}>
						<h4 data-testid={title.replace(/\s+/g, '')}>{title}</h4>
					</GridItem>
					{renderNodeCardsContent(devices)}
				</>
			);
		}
	};


	const renderNonoperationalNodeBlock = (devices: INode[], title: string): JSX.Element => {
		if (devices.length === 0) {
			return null;
		} else {
			return (
				<>
					<GridItem xs={12}>
						<h4 data-testid={title.replace(/\s+/g, '')}>{title}</h4>
					</GridItem>
					{renderNonoperationalNodeCardsContent(devices)}
				</>
			);
		}
	};
	const renderNodeSearchBar = (): JSX.Element => {
		if (!measuringDevices.length && !infrastructureDevices.length && !unknownDevices.length) return null;
		return <NodeSearchBar systemId={system.ID} />;
	};

	return (
		<GridContainer data-testid='nodeShortStatusContainer'>
			{renderNodeSearchBar()}
			{renderNodeBlock(filteredMeasuringDevices, 'Measuring devices')}
			{renderNodeBlock(filteredInfrastructureDevices, 'Infrastructure devices')}
			{renderNonoperationalNodeBlock(filteredNonoperationalDevices, 'Nonoperational devices')}
			{renderNodeBlock(filteredUnknownDevices, 'Unknown')}
		</GridContainer>
	);
};

const mapStateToProps = (state) => {
	return {
		nodes: state.dashboard.nodes
	};
};
export default connect(mapStateToProps, null)(NodeCards);
